/**
 * Static floor-plan geometry for Novotel on Collins — Conference Centre Level 3.
 * Modelled from the venue floor-plan reference (Expo Hall, Plenary, Sponsors Space,
 * Swanston / Degraves boardrooms, Collins & Elizabeth rooms, hotel reception).
 *
 * Coordinate system: X = east, Z = south, Y = up.
 * Origin is roughly the centre of the Sponsors Space.
 *
 * The 23 numbered booth pads follow the conference floor-plan reference: the spare
 * pad 1 and stands 2–13 sit in Expo Hall, while stands 14–23 run clockwise around
 * Sponsors Space from the Expo Hall entrance. The layout remains ready for final-plan review.
 */

export type BoothFacing = 'east' | 'west' | 'north' | 'south';
export type DoorSide = 'north' | 'south' | 'east' | 'west';

export interface BoothDef {
  stand: number;
  x: number;
  z: number;
  /** Booth footprint (width along X, depth along Z) */
  width: number;
  depth: number;
  /** Which way the open face of the booth points into the room */
  facing: BoothFacing;
  category: 'platinum' | 'gold' | 'silver' | 'standard';
  location: 'Expo Hall' | 'Sponsors Space';
  isAvailable?: boolean;
}

export interface RoomDef {
  id: string;
  name: string;
  shortName: string;
  x: number;
  z: number;
  width: number;
  depth: number;
  height: number;
  floorColor: string;
  wallColor: string;
  description: string;
  /** Wall that has the door opening */
  doorSide: DoorSide;
}

/** Rooms that host seated conference sessions. */
export type PresentationRoomId =
  | 'plenary'
  | 'collins'
  | 'elizabeth'
  | 'swanston'
  | 'degraves';

/** A single avatar seating target within a presentation room. */
export interface RoomSeat {
  id: string;
  roomId: PresentationRoomId;
  x: number;
  z: number;
  /** Avatar forward heading in radians, using the floor-plan coordinate system. */
  facing: number;
}

export interface CameraPreset {
  id: string;
  label: string;
  position: [number, number, number];
  target: [number, number, number];
}

export interface FloorPlate {
  x: number;
  z: number;
  width: number;
  depth: number;
  floorColor: string;
}

export interface WallSegment {
  x1: number;
  z1: number;
  x2: number;
  z2: number;
  height?: number;
  thickness?: number;
}

export type VirtualScreenStatus = 'live' | 'upcoming' | 'ended' | 'idle';

/** Timeline-ready display metadata for a wall-mounted room screen. */
export interface VirtualScreenContent {
  roomId: string;
  sessionTitle: string;
  presenter?: string | null;
  startTime?: string | null;
  endTime?: string | null;
  status: VirtualScreenStatus;
  statusBadge?: string;
  nextSessionTitle?: string | null;
  /** Reserved for future rich display modes such as slides, video, and Q&A. */
  extensions?: {
    slideTitle?: string | null;
    videoUrl?: string | null;
    qAndAPrompt?: string | null;
  };
}

/** Absolute wall-mount geometry. The screen face points along local positive Z. */
export interface VirtualScreenMount {
  roomId: string;
  x: number;
  y: number;
  z: number;
  rotationY: number;
  width: number;
  height: number;
}

// ── Camera presets ─────────────────────────────────────────────────────

export const CAMERA_PRESETS: CameraPreset[] = [
  {
    id: 'full',
    label: 'Full Floor',
    // High overhead, slightly south so labels read upright in default view
    position: [4, 95, 52],
    target: [0, 0, -2]
  },
  {
    id: 'exhibition',
    label: 'Sponsors Space',
    // Frame corridor stands + upper/lower sponsor rows
    position: [2, 40, 30],
    target: [2, 0, -2]
  },
  {
    id: 'mainstage',
    label: 'Plenary Room',
    position: [-18, 28, 36],
    target: [-28, 1, 12]
  },
  {
    id: 'entrance',
    label: 'Reception',
    position: [48, 32, 12],
    target: [30, 0, 4]
  }
];

// ── Composite floor plates (Level 3 footprint) ──────────────────────────

export const FLOOR_PLATES: FloorPlate[] = [
  // West wing — Expo Hall + Plenary Room
  { x: -42, z: -28, width: 30, depth: 56, floorColor: '#ebe4d8' },
  // Central sponsors / foyer band
  { x: -12, z: -28, width: 40, depth: 56, floorColor: '#e7e0d4' },
  // East wing — Collins, Elizabeth, hotel reception corridor
  { x: 28, z: -10, width: 34, depth: 42, floorColor: '#e9e4da' }
];

/** Bounding box used for lighting / camera limits */
export const FLOOR_BOUNDS = {
  minX: -42,
  maxX: 62,
  minZ: -28,
  maxZ: 28,
  centerX: 8,
  centerZ: 0
};

// ── Room definitions (Level 3 labels) ───────────────────────────────────

export const ROOMS: RoomDef[] = [
  {
    id: 'expo-hall',
    name: 'Expo Hall',
    shortName: 'Expo Hall',
    x: -30,
    z: -14,
    width: 22,
    depth: 20,
    height: 5,
    floorColor: '#d5e3f0',
    wallColor: '#5b8bb8',
    description: 'Main exhibition hall for sponsor and product showcases',
    doorSide: 'east'
  },
  {
    id: 'plenary',
    name: 'Plenary Room',
    shortName: 'Plenary',
    x: -30,
    z: 12,
    width: 22,
    depth: 22,
    height: 5.5,
    floorColor: '#e5d6c8',
    wallColor: '#b89b6b',
    description: 'Plenary theatre for keynotes and closing sessions',
    doorSide: 'east'
  },
  {
    id: 'swanston',
    name: 'Swanston Boardroom',
    shortName: 'Swanston',
    x: -2,
    z: -22,
    width: 14,
    depth: 10,
    height: 3.6,
    floorColor: '#d6c8e5',
    wallColor: '#8b6bb8',
    description: 'North boardroom for executive and partner meetings',
    doorSide: 'south'
  },
  {
    id: 'degraves',
    name: 'Degraves Boardroom',
    shortName: 'Degraves',
    x: 14,
    z: -20,
    width: 12,
    depth: 10,
    height: 3.6,
    floorColor: '#c8e5e0',
    wallColor: '#6bb8a8',
    description: 'Boardroom for workshops and breakout sessions',
    doorSide: 'south'
  },
  {
    id: 'collins',
    name: 'Collins Room',
    shortName: 'Collins',
    x: 8,
    z: 20,
    width: 14,
    depth: 12,
    height: 4,
    floorColor: '#d4e0c8',
    wallColor: '#6b9b6b',
    description: 'South meeting room off the main foyer',
    doorSide: 'north'
  },
  {
    id: 'elizabeth',
    name: 'Elizabeth Room',
    shortName: 'Elizabeth',
    x: 28,
    z: 20,
    width: 16,
    depth: 12,
    height: 4,
    floorColor: '#e0d4c8',
    wallColor: '#a08060',
    description: 'East meeting room near hotel reception',
    doorSide: 'north'
  }
];

/**
 * Screen faces point into each room: north-wall mounts face south, and
 * south-wall mounts face north. Anchors are inset from the physical wall to
 * keep the rear mounting plate visible in the 3D model.
 */
export const VIRTUAL_SCREEN_MOUNTS: VirtualScreenMount[] = [
  { roomId: 'expo-hall', x: -30, y: 2.8, z: -23.78, rotationY: 0, width: 6.8, height: 3.35 },
  { roomId: 'plenary', x: -30, y: 3.05, z: 1.22, rotationY: 0, width: 7.2, height: 3.55 },
  { roomId: 'swanston', x: -2, y: 2.12, z: -26.78, rotationY: 0, width: 4.8, height: 2.35 },
  { roomId: 'degraves', x: 14, y: 2.12, z: -24.78, rotationY: 0, width: 4.5, height: 2.2 },
  { roomId: 'collins', x: 8, y: 2.3, z: 25.78, rotationY: Math.PI, width: 5.1, height: 2.5 },
  { roomId: 'elizabeth', x: 28, y: 2.3, z: 25.78, rotationY: Math.PI, width: 5.6, height: 2.75 },
];

export const DEFAULT_VIRTUAL_SCREEN_CONTENT: VirtualScreenContent = {
  roomId: '',
  sessionTitle: 'Room Available',
  status: 'idle',
};

interface SeatGridConfig {
  roomId: PresentationRoomId;
  startX: number;
  startZ: number;
  columns: number;
  rows: number;
  columnGap: number;
  rowGap: number;
  facing: number;
}

function createSeatGrid({
  roomId,
  startX,
  startZ,
  columns,
  rows,
  columnGap,
  rowGap,
  facing,
}: SeatGridConfig): RoomSeat[] {
  const seats: RoomSeat[] = [];
  for (let row = 0; row < rows; row += 1) {
    for (let column = 0; column < columns; column += 1) {
      seats.push({
        id: `${roomId}-seat-${row + 1}-${column + 1}`,
        roomId,
        x: startX + column * columnGap,
        z: startZ + row * rowGap,
        facing,
      });
    }
  }
  return seats;
}

/**
 * Seating targets for the presentation rooms. Coordinates stay clear of doors
 * and walls, while `facing` keeps each audience row oriented toward its stage
 * or meeting focal point.
 */
export const ROOM_SEATING_GRIDS: Readonly<Record<PresentationRoomId, readonly RoomSeat[]>> = {
  // Theatre rows face west toward the plenary stage.
  plenary: createSeatGrid({
    roomId: 'plenary',
    startX: -24,
    startZ: 4,
    columns: 5,
    rows: 7,
    columnGap: -3,
    rowGap: 2.7,
    facing: -Math.PI / 2,
  }),
  // Boardroom rows face the presentation wall away from the south door.
  swanston: createSeatGrid({
    roomId: 'swanston',
    startX: -5.5,
    startZ: -24.3,
    columns: 3,
    rows: 2,
    columnGap: 3.5,
    rowGap: 3.2,
    facing: Math.PI,
  }),
  degraves: createSeatGrid({
    roomId: 'degraves',
    startX: 10.5,
    startZ: -22.8,
    columns: 3,
    rows: 2,
    columnGap: 3.4,
    rowGap: 3.2,
    facing: Math.PI,
  }),
  // Workshop seats face south, leaving a clear aisle by the north doorway.
  collins: createSeatGrid({
    roomId: 'collins',
    startX: 4,
    startZ: 18.2,
    columns: 4,
    rows: 2,
    columnGap: 3,
    rowGap: 3.4,
    facing: 0,
  }),
  elizabeth: createSeatGrid({
    roomId: 'elizabeth',
    startX: 23,
    startZ: 18.2,
    columns: 4,
    rows: 2,
    columnGap: 3.3,
    rowGap: 3.4,
    facing: 0,
  }),
};

// ── Registration / sponsor desk (beige counter by restrooms) ────────────

export const REGISTRATION_DESK = {
  x: 2,
  z: 1,
  width: 2.2,
  depth: 10,
  height: 1.15,
  color: '#c4b59a',
  label: 'Sponsors Desk'
};

// ── Restroom block (visual only) ────────────────────────────────────────

export const RESTROOM_BLOCK = {
  x: 12,
  z: 1,
  width: 10,
  depth: 10,
  height: 3.2,
  color: '#d8d2c8',
  wallColor: '#b0a89c'
};

// ── Hotel reception corridor marker ─────────────────────────────────────

export const HOTEL_RECEPTION = {
  x: 48,
  z: 2,
  width: 18,
  depth: 8,
  label: 'Hotel Reception →'
};

// ── Structural walls (corridor / core partitions beyond room boxes) ─────

export const STRUCTURAL_WALLS: WallSegment[] = [
  // Partition between Expo/Plenary wing and sponsors corridor (with booth gaps implied)
  { x1: -18, z1: -24, x2: -18, z2: -18, height: 3.5 },
  { x1: -18, z1: -8, x2: -18, z2: -2, height: 3.5 },
  { x1: -18, z1: 4, x2: -18, z2: 10, height: 3.5 },
  { x1: -18, z1: 16, x2: -18, z2: 24, height: 3.5 },
  // Core / restroom west face
  { x1: 7, z1: -4, x2: 7, z2: 6, height: 3.2 },
  // North corridor wall stubs
  { x1: -10, z1: -17, x2: 8, z2: -17, height: 3.2 },
  // South corridor wall stubs near Collins
  { x1: 0, z1: 13, x2: 18, z2: 13, height: 3.2 },
  // East reception corridor north edge
  { x1: 28, z1: -4, x2: 58, z2: -4, height: 3 },
  // East reception corridor south edge
  { x1: 36, z1: 8, x2: 58, z2: 8, height: 3 }
];

// ── Booth layout: numbered dark pads from the conference floor plan ─────────
// Stand 1 is the spare pad above the Expo Hall entry. Stands 2–13 continue
// counter-clockwise through Expo Hall; stands 14–23 begin at that entry and run
// clockwise around Sponsors Space. Categories remain provisional sponsor tiers.

function buildBooths(): BoothDef[] {
  return [
    // Expo Hall — counter-clockwise from the spare pad above the east entry.
    { stand: 1, x: -20.7, z: -16.2, width: 2.6, depth: 2.6, facing: 'west', category: 'standard', location: 'Expo Hall', isAvailable: true },
    { stand: 2, x: -23.3, z: -22.0, width: 3.6, depth: 2.4, facing: 'south', category: 'gold', location: 'Expo Hall' },
    { stand: 3, x: -29.0, z: -22.0, width: 3.6, depth: 2.4, facing: 'south', category: 'gold', location: 'Expo Hall' },
    { stand: 4, x: -34.7, z: -22.0, width: 3.6, depth: 2.4, facing: 'south', category: 'silver', location: 'Expo Hall' },
    { stand: 5, x: -39.1, z: -18.1, width: 2.4, depth: 3.1, facing: 'east', category: 'standard', location: 'Expo Hall' },
    { stand: 6, x: -39.1, z: -14.0, width: 2.4, depth: 3.1, facing: 'east', category: 'platinum', location: 'Expo Hall' },
    { stand: 7, x: -39.1, z: -9.5, width: 2.4, depth: 3.1, facing: 'east', category: 'gold', location: 'Expo Hall' },
    { stand: 8, x: -39.1, z: -5.8, width: 2.4, depth: 2.5, facing: 'east', category: 'silver', location: 'Expo Hall' },
    { stand: 9, x: -35.3, z: -5.4, width: 3.3, depth: 2.3, facing: 'north', category: 'standard', location: 'Expo Hall' },
    { stand: 10, x: -30.7, z: -5.4, width: 3.3, depth: 2.3, facing: 'north', category: 'standard', location: 'Expo Hall' },
    { stand: 11, x: -26.1, z: -5.4, width: 3.3, depth: 2.3, facing: 'north', category: 'gold', location: 'Expo Hall' },
    { stand: 12, x: -30.4, z: -11.0, width: 3.5, depth: 2.5, facing: 'south', category: 'silver', location: 'Expo Hall' },
    { stand: 13, x: -22.8, z: -11.0, width: 2.6, depth: 2.6, facing: 'east', category: 'standard', location: 'Expo Hall' },
    // Sponsors Space — clockwise from the Expo Hall entrance.
    { stand: 14, x: -10.0, z: -8.6, width: 2.6, depth: 2.6, facing: 'east', category: 'standard', location: 'Sponsors Space' },
    { stand: 15, x: -3.6, z: -11.6, width: 3.5, depth: 2.3, facing: 'south', category: 'gold', location: 'Sponsors Space' },
    { stand: 16, x: 4.1, z: -11.6, width: 3.5, depth: 2.3, facing: 'south', category: 'platinum', location: 'Sponsors Space' },
    { stand: 17, x: 12.0, z: 10.2, width: 2.5, depth: 3.1, facing: 'west', category: 'standard', location: 'Sponsors Space' },
    { stand: 18, x: 6.5, z: 10.2, width: 3.4, depth: 2.3, facing: 'north', category: 'gold', location: 'Sponsors Space' },
    { stand: 19, x: 1.7, z: 10.2, width: 3.4, depth: 2.3, facing: 'north', category: 'silver', location: 'Sponsors Space' },
    { stand: 20, x: -3.1, z: 10.2, width: 3.4, depth: 2.3, facing: 'north', category: 'standard', location: 'Sponsors Space' },
    { stand: 21, x: -9.7, z: 7.2, width: 2.5, depth: 3.0, facing: 'east', category: 'gold', location: 'Sponsors Space' },
    { stand: 22, x: -9.7, z: 2.2, width: 2.5, depth: 3.0, facing: 'east', category: 'silver', location: 'Sponsors Space' },
    { stand: 23, x: -9.7, z: -3.2, width: 2.5, depth: 3.0, facing: 'east', category: 'standard', location: 'Sponsors Space' }
  ];
}

export const BOOTHS: BoothDef[] = buildBooths();

// ── Sponsor category colors ─────────────────────────────────────────────

export const BOOTH_COLORS: Record<
  BoothDef['category'],
  { frame: string; roof: string; accent: string; label: string }
> = {
  platinum: { frame: '#e5e4e2', roof: '#a8a4a0', accent: '#6366f1', label: 'Platinum' },
  gold: { frame: '#ffd700', roof: '#c9a700', accent: '#f59e0b', label: 'Gold' },
  silver: { frame: '#c0c0c0', roof: '#909090', accent: '#6b7280', label: 'Silver' },
  standard: { frame: '#3b82f6', roof: '#2563eb', accent: '#60a5fa', label: 'Standard' }
};

// ── Corridor / aisle floor strips ───────────────────────────────────────

export const AISLES = [
  // N–S aisle between Expo/Plenary east wall and corridor-facing stands
  { x: -17.2, z: -1.5, width: 3.4, depth: 30, color: '#f3efe6' },
  // Upper Sponsors Space cross aisle (south of stands 2–3)
  { x: 4, z: -10.7, width: 30, depth: 3.2, color: '#f3efe6' },
  // Lower Sponsors Space cross aisle (north of stands 7–10)
  { x: 8, z: 7.4, width: 34, depth: 3.2, color: '#f3efe6' },
  // Central sponsors foyer band
  { x: 4, z: -1, width: 28, depth: 5, color: '#f3efe6' },
  // Foyer toward hotel reception
  { x: 40, z: 2, width: 28, depth: 6, color: '#f0ebe0' },
  // North approach to boardrooms
  { x: 4, z: -18, width: 24, depth: 3.5, color: '#f3efe6' },
  // South approach to Collins / Elizabeth
  { x: 16, z: 14, width: 30, depth: 3.5, color: '#f3efe6' }
];

/** @deprecated Use FLOOR_PLATES — kept for any residual imports */
export const EXHIBITION_HALL = {
  x: FLOOR_BOUNDS.minX,
  z: FLOOR_BOUNDS.minZ,
  width: FLOOR_BOUNDS.maxX - FLOOR_BOUNDS.minX,
  depth: FLOOR_BOUNDS.maxZ - FLOOR_BOUNDS.minZ,
  floorColor: '#e7e0d4',
  wallColor: '#c4b8a4'
};

// ── Navigation graph (doorways, corridors, room interiors) ──────────────

export type NavNodeKind = 'corridor' | 'door' | 'door-approach' | 'room' | 'poi';

export interface NavNode {
  id: string;
  x: number;
  z: number;
  kind: NavNodeKind;
  /** Present for room / door nodes tied to a RoomDef */
  roomId?: string;
}

export interface NavGraphAdjacency {
  id: string;
  cost: number;
}

export interface NavGraph {
  nodes: NavNode[];
  nodeById: Map<string, NavNode>;
  adjacency: Map<string, NavGraphAdjacency[]>;
}

/** Axis-aligned line segment on the floor plane (Y ignored). */
export interface ObstacleSegment {
  x1: number;
  z1: number;
  x2: number;
  z2: number;
}

/** Matches room wall door cut used by conference-twin-scene. */
export function roomDoorGap(room: RoomDef): number {
  return Math.min(4, room.width * 0.35, room.depth * 0.35);
}

/** World-space door opening centre and inward/outward approach points. */
export function roomDoorPoints(room: RoomDef): {
  doorX: number;
  doorZ: number;
  inwardX: number;
  inwardZ: number;
  outwardX: number;
  outwardZ: number;
  gap: number;
  side: DoorSide;
} {
  const halfW = room.width / 2;
  const halfD = room.depth / 2;
  const gap = roomDoorGap(room);
  const clearance = Math.max(1.2, Math.min(1.8, gap * 0.4));

  switch (room.doorSide) {
    case 'east':
      return {
        doorX: room.x + halfW,
        doorZ: room.z,
        inwardX: room.x + halfW - clearance,
        inwardZ: room.z,
        outwardX: room.x + halfW + clearance,
        outwardZ: room.z,
        gap,
        side: room.doorSide
      };
    case 'west':
      return {
        doorX: room.x - halfW,
        doorZ: room.z,
        inwardX: room.x - halfW + clearance,
        inwardZ: room.z,
        outwardX: room.x - halfW - clearance,
        outwardZ: room.z,
        gap,
        side: room.doorSide
      };
    case 'south':
      return {
        doorX: room.x,
        doorZ: room.z + halfD,
        inwardX: room.x,
        inwardZ: room.z + halfD - clearance,
        outwardX: room.x,
        outwardZ: room.z + halfD + clearance,
        gap,
        side: room.doorSide
      };
    case 'north':
    default:
      return {
        doorX: room.x,
        doorZ: room.z - halfD,
        inwardX: room.x,
        inwardZ: room.z - halfD + clearance,
        outwardX: room.x,
        outwardZ: room.z - halfD - clearance,
        gap,
        side: room.doorSide
      };
  }
}

/** Room perimeter wall segments with a centred door gap on `doorSide`. */
export function roomWallObstacleSegments(room: RoomDef): ObstacleSegment[] {
  const halfW = room.width / 2;
  const halfD = room.depth / 2;
  const left = room.x - halfW;
  const right = room.x + halfW;
  const north = room.z - halfD;
  const south = room.z + halfD;
  const gap = roomDoorGap(room);
  const segs: ObstacleSegment[] = [];

  const addSide = (side: DoorSide, full: ObstacleSegment, withDoor: boolean) => {
    if (!withDoor) {
      segs.push(full);
      return;
    }
    if (side === 'north' || side === 'south') {
      const z = full.z1;
      segs.push({ x1: left, z1: z, x2: room.x - gap / 2, z2: z });
      segs.push({ x1: room.x + gap / 2, z1: z, x2: right, z2: z });
    } else {
      const x = full.x1;
      segs.push({ x1: x, z1: north, x2: x, z2: room.z - gap / 2 });
      segs.push({ x1: x, z1: room.z + gap / 2, x2: x, z2: south });
    }
  };

  addSide('north', { x1: left, z1: north, x2: right, z2: north }, room.doorSide === 'north');
  addSide('south', { x1: left, z1: south, x2: right, z2: south }, room.doorSide === 'south');
  addSide('east', { x1: right, z1: north, x2: right, z2: south }, room.doorSide === 'east');
  addSide('west', { x1: left, z1: north, x2: left, z2: south }, room.doorSide === 'west');

  return segs;
}

function rectPerimeter(
  cx: number,
  cz: number,
  width: number,
  depth: number,
): ObstacleSegment[] {
  const halfW = width / 2;
  const halfD = depth / 2;
  const left = cx - halfW;
  const right = cx + halfW;
  const north = cz - halfD;
  const south = cz + halfD;
  return [
    { x1: left, z1: north, x2: right, z2: north },
    { x1: right, z1: north, x2: right, z2: south },
    { x1: right, z1: south, x2: left, z2: south },
    { x1: left, z1: south, x2: left, z2: north }
  ];
}

/** All solid wall segments avatars must not cross. */
export function buildNavigationObstacles(): ObstacleSegment[] {
  const segs: ObstacleSegment[] = [];

  for (const wall of STRUCTURAL_WALLS) {
    segs.push({ x1: wall.x1, z1: wall.z1, x2: wall.x2, z2: wall.z2 });
  }
  for (const room of ROOMS) {
    segs.push(...roomWallObstacleSegments(room));
  }
  segs.push(
    ...rectPerimeter(
      RESTROOM_BLOCK.x,
      RESTROOM_BLOCK.z,
      RESTROOM_BLOCK.width,
      RESTROOM_BLOCK.depth,
    ),
  );
  segs.push(
    ...rectPerimeter(
      REGISTRATION_DESK.x,
      REGISTRATION_DESK.z,
      REGISTRATION_DESK.width,
      REGISTRATION_DESK.depth,
    ),
  );
  for (const booth of BOOTHS) {
    segs.push(...rectPerimeter(booth.x, booth.z, booth.width, booth.depth));
  }

  return segs;
}

function orient(
  ax: number,
  az: number,
  bx: number,
  bz: number,
  cx: number,
  cz: number,
): number {
  const v = (bz - az) * (cx - bx) - (bx - ax) * (cz - bz);
  if (Math.abs(v) < 1e-9) return 0;
  return v > 0 ? 1 : 2;
}

function onSegment(
  ax: number,
  az: number,
  bx: number,
  bz: number,
  cx: number,
  cz: number,
): boolean {
  return (
    bx <= Math.max(ax, cx) + 1e-9 &&
    bx >= Math.min(ax, cx) - 1e-9 &&
    bz <= Math.max(az, cz) + 1e-9 &&
    bz >= Math.min(az, cz) - 1e-9
  );
}

/** True when open segments AB and CD properly intersect (shared endpoints allowed). */
export function segmentsIntersect(
  a: ObstacleSegment,
  b: ObstacleSegment,
): boolean {
  const o1 = orient(a.x1, a.z1, a.x2, a.z2, b.x1, b.z1);
  const o2 = orient(a.x1, a.z1, a.x2, a.z2, b.x2, b.z2);
  const o3 = orient(b.x1, b.z1, b.x2, b.z2, a.x1, a.z1);
  const o4 = orient(b.x1, b.z1, b.x2, b.z2, a.x2, a.z2);

  if (o1 !== o2 && o3 !== o4) {
    // Ignore near-touching at shared endpoints so graph nodes on walls still link
    const endpointTouch =
      (Math.hypot(a.x1 - b.x1, a.z1 - b.z1) < 0.05) ||
      (Math.hypot(a.x1 - b.x2, a.z1 - b.z2) < 0.05) ||
      (Math.hypot(a.x2 - b.x1, a.z2 - b.z1) < 0.05) ||
      (Math.hypot(a.x2 - b.x2, a.z2 - b.z2) < 0.05);
    return !endpointTouch;
  }

  if (o1 === 0 && onSegment(a.x1, a.z1, b.x1, b.z1, a.x2, a.z2)) return true;
  if (o2 === 0 && onSegment(a.x1, a.z1, b.x2, b.z2, a.x2, a.z2)) return true;
  if (o3 === 0 && onSegment(b.x1, b.z1, a.x1, a.z1, b.x2, b.z2)) return true;
  if (o4 === 0 && onSegment(b.x1, b.z1, a.x2, a.z2, b.x2, b.z2)) return true;
  return false;
}

export function edgeBlockedByObstacles(
  x1: number,
  z1: number,
  x2: number,
  z2: number,
  obstacles: ObstacleSegment[],
): boolean {
  // Shrink slightly so nodes sitting just inside a wall don't false-positive
  const dx = x2 - x1;
  const dz = z2 - z1;
  const len = Math.hypot(dx, dz);
  if (len < 1e-6) return true;
  const inset = Math.min(0.15, len * 0.08);
  const ux = dx / len;
  const uz = dz / len;
  const trimmed: ObstacleSegment = {
    x1: x1 + ux * inset,
    z1: z1 + uz * inset,
    x2: x2 - ux * inset,
    z2: z2 - uz * inset
  };
  for (const obs of obstacles) {
    if (segmentsIntersect(trimmed, obs)) return true;
  }
  return false;
}

function dist2(ax: number, az: number, bx: number, bz: number): number {
  const dx = ax - bx;
  const dz = az - bz;
  return Math.hypot(dx, dz);
}

function pushUniqueNode(nodes: NavNode[], node: NavNode, mergeRadius = 0.75): NavNode {
  for (const existing of nodes) {
    if (dist2(existing.x, existing.z, node.x, node.z) <= mergeRadius) {
      return existing;
    }
  }
  nodes.push(node);
  return node;
}

function addCorridorStrip(
  nodes: NavNode[],
  idPrefix: string,
  x: number,
  z: number,
  width: number,
  depth: number,
  spacing = 4.5,
): void {
  const horizontal = width >= depth;
  if (horizontal) {
    const steps = Math.max(1, Math.round(width / spacing));
    const startX = x - width / 2 + width / (steps * 2);
    for (let i = 0; i < steps; i += 1) {
      pushUniqueNode(nodes, {
        id: `${idPrefix}-${i}`,
        x: startX + (i * width) / steps,
        z,
        kind: 'corridor'
      });
    }
  } else {
    const steps = Math.max(1, Math.round(depth / spacing));
    const startZ = z - depth / 2 + depth / (steps * 2);
    for (let i = 0; i < steps; i += 1) {
      pushUniqueNode(nodes, {
        id: `${idPrefix}-${i}`,
        x,
        z: startZ + (i * depth) / steps,
        kind: 'corridor'
      });
    }
  }
}

/**
 * Build the conference floor waypoint graph:
 * room centres, door openings, exterior approaches, aisle samples, POIs.
 * Edges connect nearby nodes whose straight segment does not cross solid walls.
 */
export function buildConferenceNavGraph(
  maxEdgeLength = 16,
): NavGraph {
  const nodes: NavNode[] = [];
  const obstacles = buildNavigationObstacles();

  // Room interior + doorway chain
  for (const room of ROOMS) {
    const door = roomDoorPoints(room);
    pushUniqueNode(nodes, {
      id: `room-${room.id}`,
      x: room.x,
      z: room.z,
      kind: 'room',
      roomId: room.id
    });
    // Slightly offset interior seating-side waypoints for larger rooms
    if (room.width >= 16 || room.depth >= 16) {
      const halfW = room.width * 0.22;
      const halfD = room.depth * 0.22;
      pushUniqueNode(nodes, {
        id: `room-${room.id}-a`,
        x: room.x - halfW,
        z: room.z - halfD,
        kind: 'room',
        roomId: room.id
      });
      pushUniqueNode(nodes, {
        id: `room-${room.id}-b`,
        x: room.x + halfW,
        z: room.z + halfD,
        kind: 'room',
        roomId: room.id
      });
    }
    pushUniqueNode(nodes, {
      id: `door-in-${room.id}`,
      x: door.inwardX,
      z: door.inwardZ,
      kind: 'door',
      roomId: room.id
    });
    pushUniqueNode(nodes, {
      id: `door-${room.id}`,
      x: door.doorX,
      z: door.doorZ,
      kind: 'door',
      roomId: room.id
    });
    // Extra outward clearance so approaches sit in the corridor, not on the wall
    const outDx = door.outwardX - door.doorX;
    const outDz = door.outwardZ - door.doorZ;
    const outLen = Math.hypot(outDx, outDz) || 1;
    const extra = 1.4;
    pushUniqueNode(nodes, {
      id: `door-out-${room.id}`,
      x: door.outwardX + (outDx / outLen) * extra,
      z: door.outwardZ + (outDz / outLen) * extra,
      kind: 'door-approach',
      roomId: room.id
    });
  }

  // Aisle centre-line samples
  AISLES.forEach((aisle, idx) => {
    addCorridorStrip(
      nodes,
      `aisle-${idx}`,
      aisle.x,
      aisle.z,
      aisle.width,
      aisle.depth,
      3.6,
    );
  });

  // Explicit corridor junctions / foyer anchors (placed in open circulation)
  const junctionPoints: Array<[string, number, number]> = [
    ['junc-sponsors-centre', 4, -1],
    ['junc-sponsors-north', 2, -10.7],
    ['junc-sponsors-south', 8, 7.4],
    ['junc-expo-corridor', -17.2, -1.5],
    ['junc-expo-north', -17.2, -12],
    ['junc-expo-south', -17.2, 10],
    ['junc-boardroom-approach', 4, -18],
    // South of Collins/Elizabeth doors (doors face north into this corridor)
    ['junc-collins-approach', 8, 11],
    ['junc-elizabeth-approach', 28, 11],
    ['junc-south-corridor-mid', 18, 12.5],
    ['junc-reception-mouth', 24, 2],
    ['junc-reception', 40, 2],
    ['junc-reception-east', 52, 2],
    ['junc-core-north', 4, -8],
    ['junc-core-south', 18, 2],
    ['junc-west-foyer', -8, -1],
    ['junc-east-sponsors', 16, -1]
  ];
  for (const [id, x, z] of junctionPoints) {
    pushUniqueNode(nodes, { id, x, z, kind: 'corridor' });
  }

  // Dense samples along the east reception corridor (x=22..58, z≈2)
  for (let x = 26; x <= 58; x += 4) {
    pushUniqueNode(nodes, {
      id: `reception-run-${x}`,
      x,
      z: 2,
      kind: 'corridor'
    });
  }
  // North–south link past the core into reception mouth
  for (const z of [-6, -2, 2, 6, 10]) {
    pushUniqueNode(nodes, {
      id: `core-pass-${z}`,
      x: 20,
      z,
      kind: 'corridor'
    });
  }

  // Booth approach points (open face)
  for (const booth of BOOTHS) {
    let ax = booth.x;
    let az = booth.z;
    const offset = 2.2;
    switch (booth.facing) {
      case 'west':
        ax = booth.x - booth.width / 2 - offset;
        break;
      case 'east':
        ax = booth.x + booth.width / 2 + offset;
        break;
      case 'north':
        az = booth.z - booth.depth / 2 - offset;
        break;
      case 'south':
        az = booth.z + booth.depth / 2 + offset;
        break;
    }
    pushUniqueNode(nodes, {
      id: `booth-approach-${booth.stand}`,
      x: ax,
      z: az,
      kind: 'poi'
    });
  }

  pushUniqueNode(nodes, {
    id: 'poi-reception',
    x: HOTEL_RECEPTION.x,
    z: HOTEL_RECEPTION.z,
    kind: 'poi'
  });

  const adjacency = new Map<string, NavGraphAdjacency[]>();
  for (const n of nodes) adjacency.set(n.id, []);

  const linkNodes = (a: NavNode, b: NavNode, force = false) => {
    const cost = dist2(a.x, a.z, b.x, b.z);
    if (cost < 1e-6) return;
    if (!force && cost > maxEdgeLength) return;
    if (!force && edgeBlockedByObstacles(a.x, a.z, b.x, b.z, obstacles)) return;
    const aList = adjacency.get(a.id)!;
    const bList = adjacency.get(b.id)!;
    if (!aList.some((e) => e.id === b.id)) aList.push({ id: b.id, cost });
    if (!bList.some((e) => e.id === a.id)) bList.push({ id: a.id, cost });
  };

  const nodeByIdEarly = () => new Map(nodes.map((n) => [n.id, n]));

  const linkById = (aId: string, bId: string, force = false) => {
    const map = nodeByIdEarly();
    const a = map.get(aId);
    const b = map.get(bId);
    if (!a || !b) return;
    linkNodes(a, b, force);
  };

  // Forced doorway chains (always walkable through the opening)
  for (const room of ROOMS) {
    linkById(`room-${room.id}`, `door-in-${room.id}`, true);
    linkById(`door-in-${room.id}`, `door-${room.id}`, true);
    linkById(`door-${room.id}`, `door-out-${room.id}`, true);
    linkById(`room-${room.id}-a`, `room-${room.id}`, true);
    linkById(`room-${room.id}-b`, `room-${room.id}`, true);
    linkById(`room-${room.id}-a`, `door-in-${room.id}`, true);
    linkById(`room-${room.id}-b`, `door-in-${room.id}`, true);
  }

  const isInteriorRoomNode = (n: NavNode) => n.kind === 'room' || n.kind === 'door';

  // Proximity edges
  for (let i = 0; i < nodes.length; i += 1) {
    for (let j = i + 1; j < nodes.length; j += 1) {
      const a = nodes[i]!;
      const b = nodes[j]!;
      const cost = dist2(a.x, a.z, b.x, b.z);
      if (cost > maxEdgeLength) continue;

      // Room interiors stay on their door chain — no cross-room room links
      if (a.kind === 'room' && b.kind === 'room') continue;
      // Interior room/door nodes only auto-link within the same room
      if (isInteriorRoomNode(a) && isInteriorRoomNode(b) && a.roomId && b.roomId && a.roomId !== b.roomId) {
        continue;
      }
      // Room centres do not link straight to foreign exterior nodes
      if (a.kind === 'room' && b.kind !== 'door' && b.kind !== 'door-approach') continue;
      if (b.kind === 'room' && a.kind !== 'door' && a.kind !== 'door-approach') continue;
      if (a.kind === 'room' && b.kind === 'door-approach' && a.roomId !== b.roomId) continue;
      if (b.kind === 'room' && a.kind === 'door-approach' && a.roomId !== b.roomId) continue;

      if (edgeBlockedByObstacles(a.x, a.z, b.x, b.z, obstacles)) continue;
      linkNodes(a, b, false);
    }
  }

  // Guarantee each door approach ties into the public circulation network
  const publicNodes = nodes.filter(
    (n) => n.kind === 'corridor' || n.kind === 'poi' || n.kind === 'door-approach',
  );
  for (const room of ROOMS) {
    const approach = nodes.find((n) => n.id === `door-out-${room.id}`);
    if (!approach) continue;
    const candidates = publicNodes
      .filter((n) => n.id !== approach.id)
      .map((n) => ({ n, d: dist2(approach.x, approach.z, n.x, n.z) }))
      .filter(({ n, d }) => d <= 18 && !edgeBlockedByObstacles(approach.x, approach.z, n.x, n.z, obstacles))
      .sort((a, b) => a.d - b.d)
      .slice(0, 4);
    for (const { n } of candidates) {
      linkNodes(approach, n, true);
    }
    // If still isolated from corridors, force nearest corridor even if slightly blocked
    const deg = adjacency.get(approach.id)?.length ?? 0;
    if (deg <= 1) {
      const nearest = publicNodes
        .filter((n) => n.kind === 'corridor')
        .map((n) => ({ n, d: dist2(approach.x, approach.z, n.x, n.z) }))
        .sort((a, b) => a.d - b.d)[0];
      if (nearest) linkNodes(approach, nearest.n, true);
    }
  }

  // Bridge disconnected components so A* can always route across the floor
  const nodeById = new Map(nodes.map((n) => [n.id, n]));
  const visited = new Set<string>();
  const components: string[][] = [];
  for (const n of nodes) {
    if (visited.has(n.id)) continue;
    const stack = [n.id];
    const comp: string[] = [];
    visited.add(n.id);
    while (stack.length > 0) {
      const id = stack.pop()!;
      comp.push(id);
      for (const e of adjacency.get(id) ?? []) {
        if (!visited.has(e.id)) {
          visited.add(e.id);
          stack.push(e.id);
        }
      }
    }
    components.push(comp);
  }

  if (components.length > 1) {
    // Prefer the largest component as the main walkable network
    components.sort((a, b) => b.length - a.length);
    const main = new Set(components[0]);
    for (let c = 1; c < components.length; c += 1) {
      const comp = components[c]!;
      let best: { a: NavNode; b: NavNode; d: number } | null = null;
      for (const idA of comp) {
        const a = nodeById.get(idA)!;
        if (a.kind === 'room') continue;
        for (const idB of main) {
          const b = nodeById.get(idB)!;
          if (b.kind === 'room') continue;
          const d = dist2(a.x, a.z, b.x, b.z);
          if (d > 28) continue;
          if (!best || d < best.d) best = { a, b, d };
        }
      }
      if (best) {
        linkNodes(best.a, best.b, true);
        for (const id of comp) main.add(id);
      }
    }
  }

  return { nodes, nodeById, adjacency };
}

/** Precomputed Level-3 navigation graph for the conference twin. */
export const CONFERENCE_NAV_GRAPH: NavGraph = buildConferenceNavGraph();

/** Euclidean nearest graph node to a floor position. */
export function findNearestNavNode(
  graph: NavGraph,
  x: number,
  z: number,
  predicate?: (node: NavNode) => boolean,
): NavNode | null {
  let best: NavNode | null = null;
  let bestD = Infinity;
  for (const node of graph.nodes) {
    if (predicate && !predicate(node)) continue;
    const d = dist2(node.x, node.z, x, z);
    if (d < bestD) {
      bestD = d;
      best = node;
    }
  }
  return best;
}

/**
 * A* over the conference nav graph. Returns node ids from start to goal (inclusive).
 */
export function findNavNodePath(
  graph: NavGraph,
  startId: string,
  goalId: string,
): string[] | null {
  if (startId === goalId) return [startId];
  if (!graph.nodeById.has(startId) || !graph.nodeById.has(goalId)) return null;

  const goal = graph.nodeById.get(goalId)!;
  const open = new Set<string>([startId]);
  const cameFrom = new Map<string, string>();
  const gScore = new Map<string, number>([[startId, 0]]);
  const fScore = new Map<string, number>([
    [startId, dist2(graph.nodeById.get(startId)!.x, graph.nodeById.get(startId)!.z, goal.x, goal.z)]
  ]);

  while (open.size > 0) {
    let current: string | null = null;
    let bestF = Infinity;
    for (const id of open) {
      const f = fScore.get(id) ?? Infinity;
      if (f < bestF) {
        bestF = f;
        current = id;
      }
    }
    if (!current) break;
    if (current === goalId) {
      const path = [current];
      while (cameFrom.has(path[0]!)) {
        path.unshift(cameFrom.get(path[0]!)!);
      }
      return path;
    }

    open.delete(current);
    const currentG = gScore.get(current) ?? Infinity;
    for (const edge of graph.adjacency.get(current) ?? []) {
      const tentative = currentG + edge.cost;
      if (tentative < (gScore.get(edge.id) ?? Infinity)) {
        cameFrom.set(edge.id, current);
        gScore.set(edge.id, tentative);
        const n = graph.nodeById.get(edge.id)!;
        fScore.set(edge.id, tentative + dist2(n.x, n.z, goal.x, goal.z));
        open.add(edge.id);
      }
    }
  }

  return null;
}

/**
 * High-level route: snap start/goal to nearest nodes, A* the graph, return
 * world-space waypoints (including the exact goal point).
 */
export function findNavWorldPath(
  graph: NavGraph,
  startX: number,
  startZ: number,
  goalX: number,
  goalZ: number,
): Array<{ x: number; z: number }> {
  const startNode = findNearestNavNode(graph, startX, startZ);
  const goalNode = findNearestNavNode(graph, goalX, goalZ);
  if (!startNode || !goalNode) {
    return [{ x: goalX, z: goalZ }];
  }

  const nodePath = findNavNodePath(graph, startNode.id, goalNode.id);
  if (!nodePath || nodePath.length === 0) {
    // Fallback: try routing via nearest corridor nodes if direct snap failed connectivity
    const startCorridor = findNearestNavNode(graph, startX, startZ, (n) => n.kind === 'corridor' || n.kind === 'door-approach' || n.kind === 'poi');
    const goalCorridor = findNearestNavNode(graph, goalX, goalZ, (n) => n.kind === 'corridor' || n.kind === 'door-approach' || n.kind === 'poi');
    if (startCorridor && goalCorridor) {
      const alt = findNavNodePath(graph, startCorridor.id, goalCorridor.id);
      if (alt && alt.length > 0) {
        const pts = alt.map((id) => {
          const n = graph.nodeById.get(id)!;
          return { x: n.x, z: n.z };
        });
        pts.push({ x: goalX, z: goalZ });
        return pts;
      }
    }
    return [{ x: goalX, z: goalZ }];
  }

  const points = nodePath.map((id) => {
    const n = graph.nodeById.get(id)!;
    return { x: n.x, z: n.z };
  });

  // If goal is not exactly the last node, append it
  const last = points[points.length - 1]!;
  if (dist2(last.x, last.z, goalX, goalZ) > 0.4) {
    points.push({ x: goalX, z: goalZ });
  }

  // Drop the first node if we're already essentially there
  if (points.length > 1 && dist2(startX, startZ, points[0]!.x, points[0]!.z) < 0.5) {
    points.shift();
  }

  return points.length > 0 ? points : [{ x: goalX, z: goalZ }];
}
